import { useState } from "react";
import { toast } from "react-toastify";
import { Button } from "../../../../components/ui/button";
import { ErrorBanner } from "../../hyperliquid/components/ErrorBanner";
import { QueueLayout } from "../components/QueueLayout";
import { QueueTable, Td } from "../components/table";
import { CopyHash } from "../components/CopyHash";
import { FormDialog } from "../components/FormDialog";
import {
  useWithdrawalQueue,
  useMarkSent,
  useDeclineWithdrawal,
} from "../api/hooks";
import { formatDateTime } from "../../../../lib/format";

// Withdrawals waiting on a payout decision.
//
// Every row here is money the user's MT5 account has ALREADY been debited for — the
// balance dropped when they confirmed with their PIN, not when we pay. So a row
// sitting in this queue is a user who is short until an admin acts.
//
// One table with inline actions rather than a queue plus a detail page: the queue
// row already carries every field needed to make the payment, and there is no detail
// endpoint for withdrawals.

const COLUMNS = [
  { label: "Submitted" },
  { label: "Ref", align: "right" },
  { label: "Login", align: "right" },
  { label: "Debited", align: "right" },
  { label: "Fee", align: "right" },
  { label: "Pay out", align: "right" },
  { label: "Destination" },
  { label: "", align: "right", sticky: true },
];

// Money arrives as decimal strings. Parsed to INTEGER CENTS so nothing is ever
// added or subtracted as a float — the digits are read straight out of the string
// rather than going through parseFloat. Plain numbers rather than BigInt: cents here
// are far below the safe-integer ceiling, and BigInt is not in this project's eslint
// environment.
const toCents = (value) => {
  const text = String(value ?? "").trim();
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  const [whole, frac = ""] = text.split(".");
  return Number(whole) * 100 + Number(frac.padEnd(2, "0"));
};

const money = (cents) =>
  cents == null
    ? "—"
    : `${Math.floor(cents / 100).toLocaleString()}.${String(cents % 100).padStart(2, "0")}`;

// THE figure the admin actually sends. The fee comes OUT of the payout, so paying
// `amount` overpays every user by a dollar.
const payoutOf = (row) => {
  const amount = toCents(row.amount);
  const fee = toCents(row.fee);
  return amount == null || fee == null ? null : amount - fee;
};

export function WithdrawalQueuePage() {
  const queue = useWithdrawalQueue();
  const markSent = useMarkSent();
  const decline = useDeclineWithdrawal();

  // { row, action } — the row awaiting confirmation, and which action.
  const [confirming, setConfirming] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);

  const rows = queue.data;
  const busy = markSent.isPending || decline.isPending;

  const close = () => {
    setConfirming(null);
    setAcknowledged(false);
  };

  const run = () => {
    const { row, action } = confirming;
    const isSend = action === "sent";
    const mutation = isSend ? markSent : decline;

    mutation.mutate(row.id, {
      onSuccess: () =>
        toast.success(
          isSend
            ? `Marked #${row.login} as paid`
            : `Declined #${row.login}, the money is back on the account`,
        ),
      onError: (error) => {
        // A 502/503 on a decline is the awkward one: the row is already DECLINED but
        // the refund is still owed, and nothing lists declined rows for a retry. Say
        // so plainly rather than implying it can be fixed from here.
        if (!isSend && (error?.status === 502 || error?.status === 503)) {
          toast.error(
            "Declined, but the refund did not go through. The money is still off the account — chase this outside the console.",
            { autoClose: false },
          );
        } else {
          toast.error(error?.message || "That did not work");
        }
      },
      onSettled: close,
    });
  };

  const row = confirming?.row;
  const isSend = confirming?.action === "sent";

  return (
    <QueueLayout
      title="Withdrawal"
      accent="payouts"
      description="Confirmed withdrawals waiting to be paid, oldest first. The account has already been debited, so the user is short until one of these is actioned."
      isFetching={queue.isFetching}
      onRefresh={() => queue.refetch()}
    >
      {queue.error ? (
        <ErrorBanner error={queue.error} onRetry={() => queue.refetch()} />
      ) : (
        <QueueTable
          columns={COLUMNS}
          isLoading={queue.isLoading}
          rows={rows}
          renderRow={(w) => (
            <tr key={w.id} className="transition-colors hover:bg-white/[0.02]">
              <Td className="whitespace-nowrap font-mono text-muted-foreground">
                {formatDateTime(w.createdAt)}
              </Td>
              <Td align="right" className="font-mono text-muted-foreground">
                {w.ref}
              </Td>
              <Td align="right" className="font-mono">
                {w.login}
              </Td>
              <Td align="right" className="font-mono text-muted-foreground">
                {money(toCents(w.amount))}
              </Td>
              <Td align="right" className="font-mono text-muted-foreground">
                −{money(toCents(w.fee))}
              </Td>
              {/* The only figure that should ever be pasted into a wallet. */}
              <Td align="right" className="font-mono font-semibold text-ginox-green">
                {money(payoutOf(w))}
              </Td>
              <Td>
                <CopyHash value={w.destination} head={10} tail={8} />
              </Td>
              <Td align="right" sticky>
                <span className="inline-flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => setConfirming({ row: w, action: "decline" })}
                  >
                    Decline
                  </Button>
                  <Button
                    size="sm"
                    disabled={busy}
                    onClick={() => setConfirming({ row: w, action: "sent" })}
                  >
                    Mark sent
                  </Button>
                </span>
              </Td>
            </tr>
          )}
        />
      )}

      <FormDialog
        open={Boolean(confirming)}
        onOpenChange={(next) => !next && close()}
        title={isSend ? "Mark this withdrawal as sent" : "Decline this withdrawal"}
        description={
          isSend
            ? "This records a payment you have already made. It emails the user and cannot be undone."
            : "The full amount goes back onto the account and the user is emailed. No reason is recorded."
        }
        onSubmit={run}
        submitLabel={isSend ? "Mark sent" : "Decline"}
        submitDisabled={isSend && !acknowledged}
        loading={busy}
        danger={!isSend}
      >
        {row ? (
          <div className="space-y-3 rounded-md border border-border/70 bg-white/[0.02] p-4">
            <div className="flex items-center justify-between gap-4">
              <span className="text-xs text-muted-foreground">Account</span>
              <span className="font-mono text-sm">#{row.login}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-xs text-muted-foreground">
                {isSend ? "Amount to send" : "Returned to the account"}
              </span>
              <span className="font-mono text-base font-semibold text-ginox-green">
                {money(isSend ? payoutOf(row) : toCents(row.amount))}
              </span>
            </div>
            {isSend ? (
              <div className="flex items-start justify-between gap-4">
                <span className="text-xs text-muted-foreground">Destination</span>
                {/* Shown in full: this is the last chance to catch a wrong paste. */}
                <span className="break-all text-right font-mono text-xs">
                  {row.destination}
                </span>
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Marking sent records something that happened outside this system, emails
            the user, and has no undo — so it takes a deliberate second action. */}
        {isSend ? (
          <label className="flex cursor-pointer items-start gap-2.5">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-emerald-500"
            />
            <span className="text-xs text-muted-foreground">
              I have sent this payment to the address above.
            </span>
          </label>
        ) : null}
      </FormDialog>
    </QueueLayout>
  );
}
